import classNames from 'classnames';
import debounce from 'debounce-promise';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Event } from '@feathr/blackbox';
import type { IFormElementProps, ISelectOption } from '@feathr/components';
import { UniversalSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';
import type { Attributes, IListParams } from '@feathr/rachis';

import type { IEventFilters } from '../EventSwitcher/EventSwitcher';

import * as styles from './EventSelect.css';

interface IProps extends IFormElementProps {
  className?: string;
  id?: string;
  onChange: (newValue: ISelectOption) => void;
  onClear?: () => void;
  placeholder?: string;
  value: ISelectOption | undefined;
}

function EventSelect({
  className,
  id,
  helpText,
  label,
  onChange,
  onClear,
  placeholder,
  value,
}: IProps): JSX.Element {
  const { Events } = useStore();
  const { t } = useTranslation();

  const loadPagedOptions = useCallback(
    async (
      inputValue: string,
      prevOptions: readonly ISelectOption[],
      additional?: { page: number },
    ): Promise<{
      options: readonly ISelectOption[];
      hasMore: boolean;
      additional: { page: number };
    }> => {
      const currentPage = additional?.page ?? 0;

      const listParams: IListParams<Attributes<Event>> = {
        filters: {
          is_archived__ne: true,
        } as IEventFilters,
        only: ['id', 'logo', 'name'],
        ordering: ['name'],
        pagination: {
          page: currentPage,
          page_size: UniversalSelect.DEFAULT_PAGE_SIZE,
        },
      };
      if (inputValue.length) {
        listParams.filters!.name__icontains = inputValue;
      }
      const data = Events.list(listParams);
      await when(() => !data.isPending);
      const loadedOptions: ISelectOption[] = data.models.map((event: Event) => ({
        id: event.id,
        name: event.name,
      }));
      const options = [...loadedOptions];

      const hasMore = loadedOptions.length === UniversalSelect.DEFAULT_PAGE_SIZE;

      return {
        options,
        hasMore,
        additional: {
          page: currentPage + 1,
        },
      };
    },
    [Events],
  );

  const debouncedLoadOptions = useMemo(
    () => debounce(loadPagedOptions, DEFAULT_DEBOUNCE_WAIT),
    [loadPagedOptions],
  );

  function handleChange(eventSelected: ISelectOption): void {
    onChange(eventSelected);
  }

  function handleOptionsMessage({ inputValue }): string {
    return inputValue.length ? t('No results...') : t('Start typing the name of a project...');
  }

  return (
    <UniversalSelect<ISelectOption>
      cacheOptions={true}
      defaultOptions={true}
      helpText={helpText}
      id={id}
      label={label}
      loadPagedOptions={debouncedLoadOptions}
      name={'event_select'}
      noOptionsMessage={handleOptionsMessage}
      onClear={onClear}
      onSelectSingle={handleChange}
      paginate={true}
      placeholder={placeholder}
      value={value}
      wrapperClassName={classNames(styles.root, className)}
    />
  );
}

export default observer(EventSelect);
